import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { getApiClient } from '~/lib/axios';
import { multipartRequest } from '~/lib/multipart';
import { csrfHeaders } from '~/lib/csrf';
import type { ApiResponse } from '~/types';
import type { HttpMethod } from '~/constants/app-data';

export function useMakeRequest<TData = ApiResponse>() {
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const queryClient = useQueryClient();

  const apiClient = getApiClient();

  const mutation = useMutation({
    mutationFn: async ({
      url,
      method,
      payload,
    }: {
      url: string;
      method: HttpMethod;
      payload?: unknown;
    }) => {
      const config = {
        method: method.toLowerCase(),
        url,
        ...(payload instanceof FormData
          ? multipartRequest(payload)
          : {
              headers: csrfHeaders(),
              ...(payload ? { data: payload } : {}),
            }),
      };

      const { data } = await apiClient(config);
      return data as ApiResponse;
    },
  });

  const makeRequest = (
    url: string,
    method: HttpMethod = 'GET',
    payload: unknown,
    onSuccess: (data: TData) => void,
    onError: (error: AxiosError) => void,
    onFinally?: () => void
  ) => {
    setLoading(true);
    setIsError(false);
    mutation.mutate(
      { url, method, payload },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: [url] });
          onSuccess?.(data as TData);
        },
        onError: async (error: Error) => {
          setIsError(true);
          onError?.(error as AxiosError);
        },
        onSettled: () => {
          onFinally?.();
          setLoading(false);
        },
      }
    );
  };

  return { makeRequest, loading, isError };
}
